import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import HeroVideo from './HeroVideo';

gsap.registerPlugin(ScrollTrigger);

/*
 * Scroll expansion adapted from "Video mask on scroll" by Mariano Pedrini
 * (https://codepen.io/marianopedrini/pen/jEbMXzp, MIT). Only the scroll-progress
 * mask concept is reused; the mask bounds are measured from the existing frame
 * so the approved first frame and image crop stay identical.
 */
const ENABLE_QUERY = '(min-width: 1024px) and (prefers-reduced-motion: no-preference)';

export default function HomeHero() {
  const sceneRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const maskRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scene = sceneRef.current;
    const section = sectionRef.current;
    const frame = frameRef.current;
    const mask = maskRef.current;
    if (!scene || !section || !frame || !mask) return undefined;

    const mm = gsap.matchMedia();
    mm.add(ENABLE_QUERY, () => {
      const headerH = () => document.querySelector('header')?.getBoundingClientRect().height ?? 72;
      const extra = () => Math.round(window.innerHeight * 1.6);
      const startRadius = parseFloat(getComputedStyle(mask).borderTopLeftRadius) || 20;

      // geometry of the frame inside the pinned section (frame itself is never transformed)
      const geo = () => {
        const s = section.getBoundingClientRect();
        const f = frame.getBoundingClientRect();
        const offTop = f.top - s.top;
        const vw = document.documentElement.clientWidth;
        return {
          top: -offTop,
          left: -f.left,
          right: -(vw - f.right),
          bottom: -(window.innerHeight - headerH() - offTop - f.height),
        };
      };

      // matches the section's lg:min-h plus the 160vh scroll distance; CSS keeps it correct on resize
      gsap.set(scene, { height: 'calc(260vh - 80px)' });
      gsap.set(section, { position: 'sticky', top: () => headerH() });

      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: scene,
          start: () => `top top+=${headerH()}`,
          end: () => `+=${extra()}`,
          scrub: 0.5,
          invalidateOnRefresh: true,
        },
      });

      tl.to({}, { duration: 0.15 })
        .fromTo(
          mask,
          { top: 0, left: 0, right: 0, bottom: 0, borderRadius: startRadius },
          {
            top: () => geo().top,
            left: () => geo().left,
            right: () => geo().right,
            bottom: () => geo().bottom,
            borderRadius: 0,
            duration: 0.6,
            ease: 'power2.inOut',
          },
          0.15,
        )
        .to(leftRef.current, { x: () => -window.innerWidth * 0.08, opacity: 0, duration: 0.4, ease: 'power1.in' }, 0.15)
        .to(rightRef.current, { x: () => window.innerWidth * 0.08, opacity: 0, duration: 0.4, ease: 'power1.in' }, 0.15)
        .to(labelsRef.current, { opacity: 0, duration: 0.2 }, 0.15)
        .to({}, { duration: 0.25 }, 0.75);

      return () => {
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    });

    let alive = true;
    document.fonts?.ready.then(() => {
      if (alive) ScrollTrigger.refresh();
    });

    return () => {
      alive = false;
      mm.revert();
    };
  }, []);

  return (
    <div ref={sceneRef} className="hero-scroll-scene relative">
      <section ref={sectionRef} className="hero-section bg-background-50 px-5 md:px-8 lg:px-12 pt-6 md:pt-8 pb-6 md:pb-8 lg:min-h-[calc(100vh-80px)] flex flex-col">
        <h1 className="sr-only">Daywell Living — your life, well lived. Thoughtful senior living.</h1>
        <div className="hero-content flex-1 w-full mx-auto max-w-[1440px] flex flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center justify-center gap-5 md:gap-6 lg:gap-8">
          <div ref={leftRef} className="hero-heading-left w-full flex lg:justify-end lg:text-right min-w-0" aria-hidden="true">
            <p className="hero-serif-roman text-[46px] sm:text-[64px] lg:text-[clamp(54px,6vw,104px)] leading-none text-foreground-950 whitespace-nowrap">
              Your life
            </p>
          </div>
          <div ref={frameRef} className="hero-media-frame relative z-10 w-full lg:w-[40vw] lg:max-w-[620px] h-[56vh] min-h-[380px] max-h-[560px] lg:h-[60vh] lg:max-h-[680px] lg:min-h-[440px] shrink-0 rounded-2xl md:rounded-[20px] bg-background-200">
            <div ref={maskRef} className="hero-media-mask absolute inset-0 rounded-2xl md:rounded-[20px] overflow-hidden bg-background-200">
              <img
                className="hero-media absolute inset-0 w-full h-full object-cover object-center"
                src="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-hero-01.webp"
                alt="Older woman sharing a quiet laugh with her daughter in a sunlit garden"
                title="Daywell Living senior living garden moment"
                fetchPriority="high"
                loading="eager"
                width={1920}
                height={1080}
                onLoad={() => ScrollTrigger.refresh()}
              />
              <HeroVideo />
            </div>
          </div>
          <div ref={rightRef} className="hero-heading-right w-full flex justify-end lg:justify-start lg:text-left min-w-0" aria-hidden="true">
            <p className="em-serif text-[50px] sm:text-[68px] lg:text-[clamp(58px,6.4vw,110px)] leading-none tracking-[-0.02em] text-foreground-950 whitespace-nowrap lg:-ml-[0.04em] lg:translate-y-[0.04em]">
              well lived.
            </p>
          </div>
        </div>
        <div ref={labelsRef} className="w-full mx-auto max-w-[1440px] mt-6 md:mt-8 pt-4 border-t border-background-200 flex items-center justify-between gap-4 text-[11px] md:text-[12px] tracking-[0.2em] uppercase text-foreground-600 font-medium">
          <span>Thoughtful Senior Living</span>
          <span className="whitespace-nowrap">Scroll to Explore &darr;</span>
        </div>
        <div className="hero-story-transition" aria-hidden="true" />
      </section>
    </div>
  );
}